import { Fragment, useId, useLayoutEffect, useRef, type SVGProps } from 'react'
import type { Radar } from 'shared'
import { usePreviousValue } from '@/hooks/use-previous-value'
import { circle, circleAll } from '../shared'
import { calcPositionForEachIndicator } from './base-hover'
import { generateLineArray } from './lines'

const DIMENSION_COLORS = ['var(--color-radar-blue)', 'var(--color-radar-green)', 'var(--color-radar-yellow)']

type Ring = 'competences' | 'vectors' | 'dimensions'

// How many of a ring's segments belong to one dimension, in ring order (18 competences, 6 vectors, 3 dimensions).
const perDimension = (ring: Ring) => (ring === 'competences' ? 6 : ring === 'vectors' ? 2 : 1)

/** One profile's shape on each ring, as SVG point lists. */
function shapes(indicators: Radar['indicators'], compare?: boolean): Record<Ring, string> {
  const lines = generateLineArray(indicators, compare)
  // Each competence polyline is a space-separated `x,y` list that opens at the centre, then its corner; dropping the
  // centre and chaining the three gives the outer shape without the spokes.
  const competences = [lines.competenceBlue, lines.competenceGreen, lines.competenceYellow]
    .flatMap((group) => group.split(' ').filter(Boolean).slice(1))
    .join(' ')
  return { competences, vectors: lines.vector.trim(), dimensions: lines.dimension.trim() }
}

/** A polygon that morphs from the shape it last painted: 250 ms, linear, on the same clock as the score bubbles. */
function MorphPolygon({ points, ...rest }: Omit<SVGProps<SVGPolygonElement>, 'points' | 'ref'> & { points: string }) {
  const { current, previous } = usePreviousValue(points, points)
  const ref = useRef<SVGPolygonElement>(null)
  // A layout effect, as in `Lines`: SMIL has to be started by hand, before the paint that already carries the new points.
  useLayoutEffect(() => {
    ref.current?.querySelector<SVGAnimateElement>('animate')?.beginElement()
  }, [current])
  return (
    <polygon ref={ref} points={current} {...rest}>
      {previous && (
        <animate attributeName="points" dur="250ms" begin="indefinite" fill="freeze" from={previous} to={current} />
      )}
    </polygon>
  )
}

/**
 * A comparison drawn as three radars, one per ring. Each profile is clipped to the ring it belongs to, so the outer
 * shape no longer floods the inner rings, and the compared profile is filled in its dimensions' colours on every ring
 * (not white on the inner ones), so vectors and dimensions read like the competences do. The compared profile is
 * opaque; the base profile sits over it on each ring as a faint fill with a thin dotted edge.
 */
export function ComparisonRings(props: { indicators: Radar['indicators']; showBase: boolean }) {
  // Ids safe inside `url(#…)`, identical on the server and the client.
  const id = `rings-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const base = shapes(props.indicators)
  const compared = shapes(props.indicators, true)

  return (
    <>
      <defs>
        {circle.map(({ type, list }) => {
          const ring = type
          const per = perDimension(ring)
          return (
            <Fragment key={ring}>
              <clipPath id={`${id}-${ring}`}>
                {list.map((d) => (
                  <path key={d} d={d} />
                ))}
              </clipPath>
              {DIMENSION_COLORS.map((color, dimension) => (
                <clipPath key={color} id={`${id}-${ring}-${dimension}`}>
                  {list.slice(dimension * per, (dimension + 1) * per).map((d) => (
                    <path key={d} d={d} />
                  ))}
                </clipPath>
              ))}
              <mask id={`${id}-${ring}-outside`} maskUnits="userSpaceOnUse" x="-60" y="-60" width="460" height="460">
                <rect x="-60" y="-60" width="460" height="460" fill="white" />
                <MorphPolygon points={compared[ring]} fill="black" />
              </mask>
              <mask id={`${id}-${ring}-inside`} maskUnits="userSpaceOnUse" x="-60" y="-60" width="460" height="460">
                <MorphPolygon points={compared[ring]} fill="white" />
              </mask>
            </Fragment>
          )
        })}
      </defs>

      {circle.map(({ type }) => {
        const ring = type
        return (
          <g key={ring}>
            {DIMENSION_COLORS.map((color, dimension) => (
              <g key={color} clipPath={`url(#${id}-${ring}-${dimension})`}>
                <MorphPolygon
                  points={compared[ring]}
                  fill={color}
                  fillOpacity={1}
                  stroke={color}
                  strokeWidth={1}
                  strokeLinejoin="round"
                />
              </g>
            ))}
            {/* The base profile over the compared one. Its fill only where it reaches past it, and strong there; its dotted
                edge soft on the open ring and strong where it crosses the compared shape, so both read at a glance. */}
            <g
              clipPath={`url(#${id}-${ring})`}
              style={{ opacity: props.showBase ? 1 : 0, transition: 'opacity 250ms linear' }}
            >
              <g mask={`url(#${id}-${ring}-outside)`}>
                <MorphPolygon points={base[ring]} fill="var(--color-radar-compare)" fillOpacity={0.3} />
              </g>
              {(['outside', 'inside'] as const).map((side) => (
                <g key={side} mask={`url(#${id}-${ring}-${side})`}>
                  <MorphPolygon
                    points={base[ring]}
                    fill="none"
                    stroke="var(--color-radar-compare)"
                    strokeOpacity={0.5}
                    strokeWidth={0.8}
                    strokeDasharray="2.5 2.5"
                    strokeLinecap={side === 'inside' ? 'round' : undefined}
                    strokeLinejoin="round"
                  />
                </g>
              ))}
            </g>
          </g>
        )
      })}

      {/* The card-coloured seams between segments, over both profiles, as the report draws them. */}
      <path fill="none" stroke="var(--color-card)" strokeMiterlimit="10" d={circleAll} />
    </>
  )
}

/** The compared profile as a plain one (its `valueToCompare` as `value`), for the scores that follow the coloured shape. */
export function shownProfile(indicators: Radar['indicators']): Radar['indicators'] {
  const ring = (key: Ring) =>
    indicators[key].map((entry) => ({
      ...entry,
      value: entry.valueToCompare ?? entry.value,
      valueToCompare: undefined,
    }))
  // The shared type is a fixed-length tuple per ring; mapping keeps each ring's length.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return {
    competences: ring('competences'),
    vectors: ring('vectors'),
    dimensions: ring('dimensions'),
  } as Radar['indicators']
}

/** The competences' names around the outer ring, placed as the report's hover titles are. */
export function IndicatorTitles(props: { indicators: Radar['indicators'] }) {
  const titles = calcPositionForEachIndicator(props.indicators, 'competences', 18, 80) ?? []
  return (
    <g>
      {titles.map(({ title, index, x, y }) =>
        title.trim() ? (
          <text
            key={index}
            fontSize="9"
            fill="var(--color-muted-foreground)"
            transform={`translate(${x} ${y})`}
            textAnchor="middle"
            // Keeps the padded 'Empoderamiento' label aligned, as the report does.
            style={{ whiteSpace: 'pre' }}
          >
            {title}
          </text>
        ) : null,
      )}
    </g>
  )
}
